import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquareWarning, Loader2, Wrench, Reply, ShieldAlert, Copy, Check, Globe, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const inputCls =
  "w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de]/60 px-4 py-3 text-sm text-[#1e3d3a] placeholder:text-[#1e3d3a]/35 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20 transition-colors duration-300";

const sev = {
  Low: "#4b7a3a", Medium: "#a3823f", High: "#c07429", Critical: "#b23a48",
};

export default function AdminReputationRepair() {
  const [company, setCompany] = useState("");
  const [reviews, setReviews] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [openReply, setOpenReply] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [website, setWebsite] = useState("");
  const [webLoading, setWebLoading] = useState(false);
  const [webResult, setWebResult] = useState(null);

  const connectWebsite = async () => {
    if (!website.trim()) { toast.error("Enter the client's website URL."); return; }
    setWebLoading(true);
    setWebResult(null);
    try {
      const { data } = await api.post("/reputation/website", {
        website: website.trim(), company, reviews: reviews.trim() || null,
      });
      setWebResult(data.result);
      const n = (data.result.reviews || []).length;
      toast.success(n ? `Drafted replies for ${n} review${n === 1 ? "" : "s"}.` : "Connected — no reviews found on that site.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't connect to that website.");
    } finally {
      setWebLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (reviews.trim().length < 10) {
      toast.error("Paste a few customer comments first.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/analyze-reviews", { company, reviews });
      setResult(data.result);
      toast.success("Issues identified.");
    } catch {
      toast.error("Analysis failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text, i) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(i);
      toast.success("Reply copied.");
      setTimeout(() => setCopiedIdx(null), 1600);
    } catch {
      toast.error("Copy failed.");
    }
  };

  const reset = () => {
    setCompany("");
    setReviews("");
    setResult(null);
    setOpenReply(null);
    setWebsite("");
    setWebResult(null);
  };

  return (
    <div data-testid="admin-reputation" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5">
        <form onSubmit={submit} data-testid="admin-review-form" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-6 space-y-4">
          <div className="flex items-center gap-3">
            <MessageSquareWarning className="h-4 w-4 text-[#c9a961]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#1e3d3a]/60">Reputation repair AI</span>
          </div>
          <p className="text-sm text-[#1e3d3a]/65 leading-relaxed">
            Connect a client&rsquo;s website to pull their reviews, or paste them below. The AI flags issues,
            prescribes fixes, and drafts a reply for every review — fixing negatives and thanking positives.
          </p>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#1e3d3a]/55 mb-2 block">Client website</label>
            <div className="flex gap-2">
              <input
                data-testid="admin-review-website"
                className={inputCls}
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="acmebakery.com"
              />
              <button
                type="button"
                data-testid="admin-review-connect"
                onClick={connectWebsite}
                disabled={webLoading}
                className="shrink-0 rounded-full bg-[#c9a961] text-[#1e3d3a] px-4 text-xs font-semibold uppercase tracking-[0.14em] hover:bg-[#1e3d3a] hover:text-white transition-colors duration-300 disabled:opacity-60 flex items-center gap-2"
              >
                {webLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
                {webLoading ? "Connecting…" : "Connect"}
              </button>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#1e3d3a]/55 mb-2 block">Client / Company</label>
            <input
              data-testid="admin-review-company"
              className={inputCls}
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Acme Bakery"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#1e3d3a]/55 mb-2 block">Negative comments</label>
            <textarea
              data-testid="admin-review-input"
              className={`${inputCls} min-h-[220px] resize-none`}
              value={reviews}
              onChange={(e) => setReviews(e.target.value)}
              placeholder="Paste reviews, BBB complaints, or support messages here…"
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              data-testid="admin-review-submit"
              disabled={loading}
              className="flex-1 rounded-full bg-[#1e3d3a] text-white py-3 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Analysing…</>) : "Diagnose"}
            </button>
            {(result || company || reviews) && !loading && (
              <button
                type="button"
                data-testid="admin-review-reset"
                onClick={reset}
                className="rounded-full border border-[#1e3d3a]/15 px-5 text-xs uppercase tracking-[0.22em] text-[#1e3d3a]/65 hover:text-[#1e3d3a] hover:border-[#1e3d3a]/40 transition-colors duration-300"
              >
                Clear
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="lg:col-span-7 space-y-4">
        {webResult && (
          <div data-testid="admin-web-result" className="rounded-2xl border border-[#c9a961]/40 bg-white p-6">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-[#a3823f]" />
              <span className="text-[10px] uppercase tracking-[0.26em] text-[#a3823f]">Website reviews · {webResult.business || "Client"}</span>
            </div>
            {webResult.summary && <p className="mt-2 text-sm text-[#1e3d3a]/70">{webResult.summary}</p>}
            {(webResult.platforms_detected || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {webResult.platforms_detected.map((p, i) => (
                  <span key={i} className="rounded-full bg-[#eae7de] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-[#1e3d3a]/70">{p}</span>
                ))}
              </div>
            )}
            {(webResult.reviews || []).length === 0 ? (
              <p className="mt-4 text-sm text-[#1e3d3a]/50">No reviews were found on that site. Paste reviews on the left and try Diagnose, or add a /reviews page URL.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {webResult.reviews.map((rv, i) => {
                  const neg = rv.sentiment === "negative";
                  return (
                    <div key={i} data-testid={`web-review-${i}`}
                      className={`rounded-xl border p-4 ${neg ? "border-[#b23a48]/30 bg-[#b23a48]/5" : "border-[#4b7a3a]/30 bg-[#4b7a3a]/5"}`}>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]" style={{ color: neg ? "#b23a48" : "#4b7a3a" }}>
                        {neg ? <ShieldAlert className="h-3.5 w-3.5" /> : <ThumbsUp className="h-3.5 w-3.5" />}
                        {neg ? "Negative" : "Positive"}{rv.source ? ` · ${rv.source}` : ""}
                      </div>
                      <p className="mt-1.5 text-sm text-[#1e3d3a]/80">“{rv.text}”</p>
                      {neg && rv.issue && <p className="mt-1.5 text-xs text-[#b23a48]">Root issue: {rv.issue}</p>}
                      <div className="mt-3 rounded-lg bg-white border border-[#1e3d3a]/10 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] uppercase tracking-[0.16em] text-[#a3823f]">Suggested reply</span>
                          <button type="button" data-testid={`web-copy-${i}`} onClick={() => copy(rv.suggested_reply, `w${i}`)}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-[#1e3d3a]/50 hover:text-[#a3823f] transition-colors duration-300">
                            {copiedIdx === `w${i}` ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} Copy
                          </button>
                        </div>
                        <p className="mt-1.5 text-sm text-[#1e3d3a]/75">{rv.suggested_reply}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <AnimatePresence mode="wait">
          {!result && !loading && !webResult && (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full min-h-[400px] rounded-2xl border border-dashed border-[#1e3d3a]/15 flex flex-col items-center justify-center text-center p-10 bg-white/40"
            >
              <ShieldAlert className="h-10 w-10 text-[#1e3d3a]/25" strokeWidth={1} />
              <p className="mt-6 text-[#1e3d3a]/55 max-w-xs text-sm">
                Diagnosed issues, root causes and suggested replies will appear here.
              </p>
            </motion.div>
          )}
          {loading && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full min-h-[400px] rounded-2xl border border-[#1e3d3a]/12 bg-white flex flex-col items-center justify-center gap-4"
            >
              <Loader2 className="h-8 w-8 text-[#c9a961] animate-spin" />
              <p className="text-[#1e3d3a]/60 text-sm">Reading between the lines…</p>
            </motion.div>
          )}
          {result && !loading && (
            <motion.div
              key="res"
              data-testid="admin-review-result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">Sentiment</span>
                  <div
                    className="mt-2 font-head text-2xl"
                    style={{ color: result.overall_sentiment === "Critical" ? "#b23a48" : "#a3823f" }}
                  >
                    {result.overall_sentiment}
                  </div>
                </div>
                <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">Satisfaction</span>
                  <div className="mt-2 font-head text-2xl font-light text-[#1e3d3a] tabular-nums">
                    {result.satisfaction_score}
                    <span className="text-base text-[#1e3d3a]/40">/100</span>
                  </div>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-[#1e3d3a]/75 rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
                {result.summary}
              </p>

              <div className="space-y-3">
                {result.issues?.map((it, i) => (
                  <div
                    key={i}
                    data-testid={`admin-issue-${i}`}
                    className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="font-head text-lg text-[#1e3d3a]">{it.issue}</h4>
                      <span
                        className="shrink-0 text-[10px] uppercase tracking-[0.2em] rounded-full px-2 py-1 border"
                        style={{ color: sev[it.severity], borderColor: `${sev[it.severity]}55` }}
                      >
                        {it.severity}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-[#1e3d3a]/55">
                      {it.frequency} · Root cause: {it.root_cause}
                    </p>
                    <div className="mt-3 flex gap-3 rounded-xl bg-[#c9a961]/8 border border-[#c9a961]/25 p-4">
                      <Wrench className="h-4 w-4 text-[#a3823f] shrink-0 mt-0.5" />
                      <p className="text-sm leading-relaxed text-[#1e3d3a]/80">{it.fix}</p>
                    </div>
                    <div className="mt-3 flex items-center gap-4">
                      <button
                        data-testid={`admin-reply-toggle-${i}`}
                        onClick={() => setOpenReply(openReply === i ? null : i)}
                        className="flex items-center gap-2 text-xs text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300"
                      >
                        <Reply className="h-3.5 w-3.5" /> {openReply === i ? "Hide" : "Show"} suggested reply
                      </button>
                      {openReply === i && (
                        <button
                          data-testid={`admin-copy-reply-${i}`}
                          onClick={() => copy(it.suggested_reply, i)}
                          className="flex items-center gap-1.5 text-xs text-[#1e3d3a]/55 hover:text-[#1e3d3a] transition-colors duration-300"
                        >
                          {copiedIdx === i ? (<><Check className="h-3.5 w-3.5" /> Copied</>) : (<><Copy className="h-3.5 w-3.5" /> Copy</>)}
                        </button>
                      )}
                    </div>
                    <AnimatePresence>
                      {openReply === i && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-3 text-sm italic leading-relaxed text-[#1e3d3a]/70 border-l-2 border-[#c9a961] pl-4 overflow-hidden"
                        >
                          &ldquo;{it.suggested_reply}&rdquo;
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
